import React, { useState, useEffect, useCallback } from "react";
import {
  Col,
  Card,
  Typography,
  Space,
  Spin,
  Modal,
  Table,
  Tag,
} from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  MinusOutlined,
  LoadingOutlined,
  ArrowLeftOutlined,
  TeamOutlined,
  BankOutlined,
} from "@ant-design/icons";
import axios from "axios";
import ENVIRONMENT from "../../../../environments";
import useTableFilter from "../../UseTableFilter/useTableFilter";

const { Title, Text } = Typography;
const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

const getTrend = (current, previous) => {
  if (current === null || current === undefined) return null;
  if (previous === 0 && current === 0)
    return { icon: <MinusOutlined />, color: "#8c8c8c", text: "No change" };
  if (previous === 0)
    return { icon: <ArrowUpOutlined />, color: "#52c41a", text: "New activity" };
  const pct = ((current - previous) / previous) * 100;
  if (pct > 0)
    return { icon: <ArrowUpOutlined />, color: "#52c41a", text: `+${pct.toFixed(1)}%` };
  if (pct < 0)
    return { icon: <ArrowDownOutlined />, color: "#f5222d", text: `${pct.toFixed(1)}%` };
  return { icon: <MinusOutlined />, color: "#8c8c8c", text: "0%" };
};

const StatCard = ({ title, icon, current, previous, isLoading, onClick, chartStyle }) => {
  const trend = getTrend(current, previous);
  return (
    <Card
      style={{ ...chartStyle, height: "100%", cursor: "pointer" }}
      onClick={onClick}
      hoverable
    >
      <Space align="center" size={10} style={{ marginBottom: 2 }}>
        {icon && (
          <span style={{ fontSize: 18, color: "#1890ff" }}>{icon}</span>
        )}
        <Title level={5} style={{ margin: 0 }}>{title}</Title>
      </Space>
      <div>
        <Text type="secondary" style={{ fontSize: 12 }}>Last 30 days</Text>
      </div>
      <Spin spinning={isLoading} indicator={antIcon}>
        <div style={{ textAlign: "center", padding: "20px 0 8px" }}>
          <div
            style={{
              fontSize: 60,
              fontWeight: 700,
              lineHeight: 1,
              background: "linear-gradient(135deg, #1890ff 0%, #69b1ff 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {current !== null && current !== undefined ? current.toLocaleString() : "—"}
          </div>
          {trend && (
            <div style={{ marginTop: 16 }}>
              <span style={{ fontSize: 18, fontWeight: 600, color: trend.color }}>
                {trend.icon} {trend.text}
              </span>
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  vs {previous !== null && previous !== undefined ? previous.toLocaleString() : "—"} previous 30 days
                </Text>
              </div>
            </div>
          )}
        </div>
      </Spin>
    </Card>
  );
};

const ActiveAIStats = ({ chartStyle }) => {
  const [stats, setStats] = useState({});
  const [isStatsLoading, setIsStatsLoading] = useState(false);

  // Companies modal — two views: "companies" list or "users" list for a company
  const [companiesModalOpen, setCompaniesModalOpen] = useState(false);
  const [companiesData, setCompaniesData] = useState([]);
  const [isCompaniesLoading, setIsCompaniesLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Users modal
  const [usersModalOpen, setUsersModalOpen] = useState(false);
  const [usersData, setUsersData] = useState([]);
  const [isUsersLoading, setIsUsersLoading] = useState(false);

  const [getColumnSearchProps] = useTableFilter();

  useEffect(() => {
    const fetchStats = async () => {
      setIsStatsLoading(true);
      try {
        const res = await axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, {
          action: "get_active_ai_stats",
        });
        setStats(res.data || {});
      } catch (e) {
        console.error("get_active_ai_stats error:", e);
        setStats({});
      } finally {
        setIsStatsLoading(false);
      }
    };
    fetchStats();
  }, []);

  const handleCompaniesCardClick = useCallback(async () => {
    setSelectedCompany(null);
    setCompaniesModalOpen(true);
    setIsCompaniesLoading(true);
    try {
      const res = await axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, {
        action: "get_active_ai_companies_details",
      });
      setCompaniesData(
        (res.data || []).map((item, i) => ({ ...item, key: i }))
      );
    } catch (e) {
      console.error("get_active_ai_companies_details error:", e);
      setCompaniesData([]);
    } finally {
      setIsCompaniesLoading(false);
    }
  }, []);

  const handleUsersCardClick = useCallback(async () => {
    setUsersModalOpen(true);
    setIsUsersLoading(true);
    try {
      const res = await axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, {
        action: "get_active_ai_users_details",
      });
      setUsersData(
        (res.data || []).map((item, i) => ({ ...item, key: i }))
      );
    } catch (e) {
      console.error("get_active_ai_users_details error:", e);
      setUsersData([]);
    } finally {
      setIsUsersLoading(false);
    }
  }, []);

  // ── Companies modal columns ────────────────────────────────────────────────
  const companiesListColumns = [
    {
      title: "Company",
      dataIndex: "company_name",
      key: "company_name",
      ...getColumnSearchProps("company_name"),
      render: (name) => <Text strong>{name}</Text>,
    },
    {
      title: "Active Users",
      dataIndex: "user_count",
      key: "user_count",
      align: "center",
      render: (count) => (
        <Tag color="blue">{count} {count === 1 ? "user" : "users"}</Tag>
      ),
    },
  ];

  const companyUsersColumns = [
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      ...getColumnSearchProps("username"),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ...getColumnSearchProps("email"),
    },
    {
      title: "Profile Name",
      dataIndex: "profile_name",
      key: "profile_name",
      ...getColumnSearchProps("profile_name"),
    },
    {
      title: "Base Images",
      dataIndex: "base_renders",
      key: "base_renders",
      align: "center",
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: "Edits",
      dataIndex: "edits",
      key: "edits",
      align: "center",
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: "Upscales",
      dataIndex: "upscales",
      key: "upscales",
      align: "center",
      render: (v) => <Text strong>{v}</Text>,
    },
  ];

  // ── Users modal columns ────────────────────────────────────────────────────
  const usersListColumns = [
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      ...getColumnSearchProps("username"),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ...getColumnSearchProps("email"),
    },
    {
      title: "Profile Name",
      dataIndex: "profile_name",
      key: "profile_name",
      ...getColumnSearchProps("profile_name"),
    },
    {
      title: "Company",
      dataIndex: "company_name",
      key: "company_name",
      ...getColumnSearchProps("company_name"),
    },
    {
      title: "Base Images",
      dataIndex: "base_renders",
      key: "base_renders",
      align: "center",
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: "Edits",
      dataIndex: "edits",
      key: "edits",
      align: "center",
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: "Upscales",
      dataIndex: "upscales",
      key: "upscales",
      align: "center",
      render: (v) => <Text strong>{v}</Text>,
    },
  ];

  const companiesModalTitle = selectedCompany ? (
    <Space>
      <ArrowLeftOutlined
        style={{ cursor: "pointer", color: "#1890ff" }}
        onClick={() => setSelectedCompany(null)}
      />
      <span>{selectedCompany.company_name}</span>
    </Space>
  ) : (
    "Active AI Companies — Last 30 Days"
  );

  return (
    <>
      <Col span={12} style={{ display: "flex", flexDirection: "column" }}>
        <StatCard
          title="Active AI Users"
          icon={<TeamOutlined />}
          current={stats.current_users}
          previous={stats.previous_users}
          isLoading={isStatsLoading}
          onClick={handleUsersCardClick}
          chartStyle={chartStyle}
        />
      </Col>

      <Col span={12} style={{ display: "flex", flexDirection: "column" }}>
        <StatCard
          title="Active AI Companies"
          icon={<BankOutlined />}
          current={stats.current_companies}
          previous={stats.previous_companies}
          isLoading={isStatsLoading}
          onClick={handleCompaniesCardClick}
          chartStyle={chartStyle}
        />
      </Col>

      {/* Companies modal — shows companies list, then drills into a company's users */}
      <Modal
        title={companiesModalTitle}
        visible={companiesModalOpen}
        onCancel={() => {
          setCompaniesModalOpen(false);
          setSelectedCompany(null);
          setCompaniesData([]);
        }}
        footer={null}
        width="80vw"
        destroyOnClose
        centered
      >
        {selectedCompany ? (
          <Table
            columns={companyUsersColumns}
            dataSource={(selectedCompany.users || []).map((u, i) => ({
              ...u,
              key: i,
            }))}
            pagination={false}
            scroll={{ y: 400 }}
            size="middle"
          />
        ) : (
          <Table
            columns={companiesListColumns}
            dataSource={companiesData}
            loading={{ indicator: antIcon, spinning: isCompaniesLoading }}
            pagination={{ pageSize: 10, size: "small" }}
            size="middle"
            style={{ marginTop: 8 }}
            onRow={(record) => ({
              onClick: () => setSelectedCompany(record),
              style: { cursor: "pointer" },
            })}
          />
        )}
      </Modal>

      {/* Users modal */}
      <Modal
        title="Active AI Users — Last 30 Days"
        visible={usersModalOpen}
        onCancel={() => {
          setUsersModalOpen(false);
          setUsersData([]);
        }}
        footer={null}
        width="80vw"
        destroyOnClose
        centered
      >
        <Table
          columns={usersListColumns}
          dataSource={usersData}
          loading={{ indicator: antIcon, spinning: isUsersLoading }}
          pagination={false}
          scroll={{ y: 400 }}
          size="middle"
        />
      </Modal>
    </>
  );
};

export default ActiveAIStats;
